/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Alert, Link } from '@zapier/design-system';

import type { ResponseData } from '../useAlertBanner/useAlertBannerData';
import { BannerProps } from './types';
import { useUniversalNavContext } from '../../../../universal-nav/hooks';

const calcDaysUntilRenewal = (renewalDate: string) => {
  const endDate = new Date(renewalDate);
  const now = new Date();
  const msBetweenDays = endDate.getTime() - now.getTime();
  return Math.round(msBetweenDays / (24 * 60 * 60 * 1000));
};

export const shouldRender = (data: ResponseData) => {
  const { currentAccount, currentUser } = data;
  if (currentAccount?.subscriptionRenewalDate === null) return false;

  /*
    This banner should only render if the user is:
    1. logged in
    2. not part of an account that pays manually (in other words an account that can "self-serve")
    3. is on a plan that bills yearly
    4. is either an "owner" or "super admin"
    5. is NOT an enterprise account
    6. is less than or equal to 30 days away from their plan renewing
  */
  return (
    currentUser.isLoggedIn &&
    !currentAccount.paysManually &&
    currentAccount.plan.interval === 'year' &&
    (currentAccount.currentUserRole === 'owner' ||
      currentAccount.currentUserRole === 'super_admin') &&
    !currentAccount.isEnterpriseAccount &&
    calcDaysUntilRenewal(currentAccount.subscriptionRenewalDate) <= 30
  );
};

export const Component = function AnnualPlanRenewalNotice(props: BannerProps) {
  const { getFullyQualifiedURL } = useUniversalNavContext();
  const currentAccount = props.data.currentAccount;
  if (currentAccount?.subscriptionRenewalDate == null) return null;

  const renewDays = calcDaysUntilRenewal(
    currentAccount.subscriptionRenewalDate
  );
  const billingSettingsURL = getFullyQualifiedURL(
    encodeURI(`/app/settings/${currentAccount.id}/billing`)
  );

  const pluralize = renewDays > 1;

  const title = (
    <span data-zds>
      Heads up, your plan renews in {renewDays} {pluralize ? 'days' : 'day'}
      {'. '}
      <Link href={billingSettingsURL} target="_blank">
        Review your plan details
      </Link>
    </span>
  );

  return <Alert layout="banner" title={title} type="info" />;
};
